// src/services/ReferralService.js

import { supabase, isDemoMode } from '../lib/supabase';
import { authService } from './AuthService';
import { checkAccessCode } from './accessCodeRpc';

class ReferralService {
  /**
   * Get referral info for a user (by user ID)
   */
  async getReferralInfo(userId) {
    if (isDemoMode) {
      return {
        data: {
          referralCode: 'DEMO_1234',
          pointsBalance: 150,
          pointsEarnedTotal: 250,
          rank: 5,
          stats: {
            total_referrals: 3,
            successful_referrals: 2,
            creator_referrals: 1,
            points_from_referrals: 200,
          },
        },
        error: null,
      };
    }

    // THE USER ID ARGUMENT IS NO LONGER TRUSTED, AND NO LONGER SENT.
    //
    // This used to call get_referral_info directly with p_user_id. That RPC
    // was EXECUTE-able by anon, so anyone holding the public key could read
    // any user's referral code, points and rank by passing someone else's id.
    // The referral-info edge function derives the user from the verified SIWE
    // token instead and accepts no id at all. `userId` is kept in the
    // signature so existing callers need no change, and is deliberately
    // unused.
    void userId;

    const authToken = authService.getToken();
    if (!authToken) {
      return {
        data: null,
        error: { message: 'Sign in with your wallet to see your referrals' },
      };
    }

    const { data, error } = await supabase.functions.invoke('referral-info', {
      headers: { 'X-Lunexx-Auth': `Bearer ${authToken}` },
    });

    if (error) {
      console.error('Error getting referral info:', error);
      return { data: null, error };
    }
    if (!data) {
      return { data: null, error: { message: 'User not found' } };
    }

    return {
      data: {
        referralCode: data.referralCode ?? null,
        pointsBalance: data.pointsBalance || 0,
        pointsEarnedTotal: data.pointsEarnedTotal || 0,
        rank: data.rank || null,
        stats: {
          total_referrals: data.stats?.total_referrals || 0,
          successful_referrals: data.stats?.successful_referrals || 0,
          creator_referrals: data.stats?.creator_referrals || 0,
          points_from_referrals: data.stats?.points_from_referrals || 0,
        },
      },
      error: null,
    };
  }

  /**
   * Get referral link for sharing
   */
  getReferralLink(referralCode) {
    if (!referralCode) return null;
    const baseUrl = window.location.origin;
    return `${baseUrl}?ref=${referralCode}`;
  }

  /**
   * Get list of users referred by this user
   */
  async getReferredUsers(userId, { limit = 10, offset = 0 } = {}) {
    if (isDemoMode) {
      return {
        data: [
          { id: '1', username: 'alice', display_name: 'Alice', is_creator: false, created_at: new Date().toISOString() },
          { id: '2', username: 'bob', display_name: 'Bob', is_creator: true, created_at: new Date(Date.now() - 86400000).toISOString() },
        ],
        error: null,
      };
    }

    const { data, error } = await supabase
      .rpc('get_referred_users', {
        p_user_id: userId,
        p_limit: limit,
        p_offset: offset,
      });

    if (error) {
      console.error('Error getting referred users:', error);
      return { data: [], error };
    }

    return { data: data || [], error: null };
  }

  /**
   * Get points transaction history
   */
  async getPointsHistory(userId, { limit = 20, offset = 0 } = {}) {
    if (isDemoMode) {
      return {
        data: [
          { id: '1', amount: 100, balance_after: 250, transaction_type: 'referral_bonus', description: 'Referral bonus for alice', created_at: new Date().toISOString() },
          { id: '2', amount: 50, balance_after: 150, transaction_type: 'signup_bonus', description: 'Welcome bonus', created_at: new Date(Date.now() - 86400000).toISOString() },
        ],
        error: null,
      };
    }

    const { data, error } = await supabase
      .rpc('get_points_history', {
        p_user_id: userId,
        p_limit: limit,
        p_offset: offset,
      });

    if (error) {
      console.error('Error getting points history:', error);
      return { data: [], error };
    }

    return { data: data || [], error: null };
  }

  /**
   * Get leaderboard of top referrers
   */
  async getLeaderboard({ limit = 10 } = {}) {
    if (isDemoMode) {
      return {
        data: [
          { user_id: '1', username: 'cryptoking', display_name: 'CryptoKing', successful_referrals: 42, points_from_referrals: 4200 },
          { user_id: '2', username: 'lunawhale', display_name: 'LunaWhale', successful_referrals: 28, points_from_referrals: 2800 },
          { user_id: '3', username: 'tradepro', display_name: 'TradePro', successful_referrals: 15, points_from_referrals: 1500 },
        ],
        error: null,
      };
    }

    const { data, error } = await supabase
      .rpc('get_referral_leaderboard', { p_limit: limit });

    if (error) {
      console.error('Error getting leaderboard:', error);
      return { data: [], error };
    }

    return { data: data || [], error: null };
  }

  /**
   * Validate a referral code
   */
  async validateReferralCode(code) {
    if (!code) return { valid: false, isReferral: false };

    if (isDemoMode) {
      return { valid: code.toUpperCase().startsWith('DEMO'), isReferral: true };
    }

    // One server-side answer (039-01) covers both the access_codes row and a
    // user's referral code; neither table is read from the browser.
    const r = await checkAccessCode(code);
    if (r.transportError) {
      return { valid: false, isReferral: false, error: r.transportError };
    }
    if (!r.valid) {
      return { valid: false, isReferral: false, error: r.message };
    }
    return { valid: true, isReferral: r.isReferral };
  }

  /**
   * Copy referral link to clipboard
   */
  async copyReferralLink(referralCode) {
    const link = this.getReferralLink(referralCode);
    if (!link) return { success: false };

    try {
      await navigator.clipboard.writeText(link);
      return { success: true };
    } catch (err) {
      console.error('Failed to copy:', err);
      return { success: false };
    }
  }

  /**
   * Share referral link using Web Share API
   */
  async shareReferralLink(referralCode, customMessage = null) {
    const link = this.getReferralLink(referralCode);
    if (!link) return { success: false };

    const shareData = {
      title: 'Join me on Lunexx',
      text: customMessage || 'Check out Lunexx - earn rewards and connect with AI agents!',
      url: link,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return { success: true };
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
        return { success: false };
      }
    } else {
      // Fallback to copy
      return this.copyReferralLink(referralCode);
    }
  }

  /**
   * Get referral code from URL params
   */
  getReferralCodeFromURL() {
    const params = new URLSearchParams(window.location.search);
    return params.get('ref') || params.get('referral') || null;
  }
}

export const referralService = new ReferralService();
export default referralService;